/**
 * Shop the Look — Contentful single-image block (KL Designs 3138-14452 / 3138-14984, hotspot 3138-14475).
 * Flyout: Figma 11204-73741 (popover / KL greyscale). Default: flush outer chrome; `standaloneContent`
 * restores page-level gutters.
 */

const ShopTheLookBlock = {
  parts: [
    'root',
    'imageWrap',
    'image',
    'rootWhenHotspotPanelOpen',
    'layerContainer',
    'layerContainerWithMarketing',
    'layerFlex',
    'imageColumn',
    'imageColumnWhenHotspotPanelOpen',
    'imageWrapWhenHotspotPanelOpen',
    'marketingTextColumn',
    'hotspotSidePanel',
    'hotspotPanelRoot',
    'hotspotPanelMediaShell',
    'hotspotPanelNativeVideo',
    'hotspotPanelImg',
    'hotspotPanelMediaFallback',
    'hotspotPanelEmbedVideo',
    'hotspotPanelTextBlock',
    'hotspotPanelTitle',
    'hotspotPanelBody',
    'hotspotPanelCloseBtn',
    'hotspotPanelNavStack',
    'hotspotPanelNavBtn',
    'hotspotAnchor',
    'hotspotDot',
    'hotspotContentDot',
    'hotspotPopoverContent',
    'hotspotPopoverBody',
    'hotspotPopoverCard',
    'hotspotPopoverLink',
    'hotspotPopoverThumb',
    'hotspotPopoverTextCol',
    'hotspotPopoverTitle',
    'hotspotPopoverMeta',
    'hotspotPopoverChevron',
    'gradientBar',
    'shopActions',
    'drawerContent',
    'drawerOverlay',
    'drawerHeroWrap',
    'drawerHeroMedia',
    'drawerHeroImage',
    'drawerHeader',
    'drawerHeaderProducts',
    'drawerProductsHeaderStack',
    'drawerHeaderRow',
    'drawerTitle',
    'drawerCloseButtonFlow',
    'drawerBodyTitleRow',
    'drawerBodyTitle',
    'drawerItemCount',
    'drawerBody',
    'drawerFooter',
    'drawerFooterBtnBag',
    'drawerProductRow',
    'drawerProductImage',
    'drawerProductImageInner',
    'drawerProductMain',
    'drawerProductTitle',
    'drawerProductTitleLink',
    'drawerProductSpec',
    'drawerSwatchRow',
    'drawerSwatchDot',
    'drawerSwatchLabel',
    'drawerPriceRow',
    'drawerCurrentPrice',
    'drawerTotalValue',
    'drawerTotalValueStrikethrough',
    'drawerProductActions',
    'drawerIconButton',
    'contentCarousel',
    'contentSlide',
    'quickAddBtn'
  ],

  baseStyle: {
    root: {
      position: 'relative',
      width: 'full',
      maxW: '100%',
      mx: 0,
      px: 0,
      py: 0,
    },

    imageWrap: {
      position: 'relative',
      width: 'full',
      borderRadius: 'none',
      overflow: 'hidden',
      lineHeight: 0,
    },

    image: {
      width: 'full',
      height: 'auto',
      objectFit: 'cover',
      display: 'block',
      verticalAlign: 'top',
    },

    rootWhenHotspotPanelOpen: {
      overflowX: {base: 'visible', md: 'visible'},
    },

    layerContainer: {
      position: 'relative',
      width: '100%',
      maxW: '100%',
      mx: 0,
      display: 'flex',
      flexDirection: {base: 'column', md: 'row'},
      alignItems: {md: 'stretch'},
      minW: 0,
    },

    layerContainerWithMarketing: {},

    layerFlex: {
      flex: {base: 'none', md: '1 1 auto'},
      minW: 0,
      display: 'flex',
      width: {md: '100%'},
      maxW: {md: '100%'},
    },

    imageColumn: {
      width: {base: 'full', md: 'auto'},
      minW: 0,
    },

    imageColumnWhenHotspotPanelOpen: {
      alignSelf: {md: 'stretch'},
      transform: {md: 'translateX(-16%)'},
      transformOrigin: {md: 'center center'},
    },

    imageWrapWhenHotspotPanelOpen: {
      overflow: {md: 'visible'},
    },

    marketingTextColumn: {
      flex: {
        base: 'none',
        md: '1 1 var(--chakra-sizes-shopTheLookMarketingCol, 24rem)',
      },
      maxW: {md: 'shopTheLookMarketingCol'},
      minW: 0,
      pt: {base: 0, md: 0},
    },

    hotspotSidePanel: {
      flex: {base: 'none', md: 'none'},
      alignSelf: {base: 'stretch', md: 'auto'},
      position: {base: 'relative', md: 'absolute'},
      top: {md: '50%'},
      right: {md: 0},
      left: {md: 'auto'},
      zIndex: {md: 12},
      w: {base: 'full', md: 56.25},
      minW: {md: 56.25},
      maxW: {md: 56.25},
      h: {md: 'shopTheLookHotspotPanelH'},
      minH: {
        base: 'min(50vh, var(--chakra-sizes-shopTheLookHotspotPanelMinVHCap, 40rem))',
        md: 'shopTheLookHotspotPanelH',
      },
      maxH: {md: 'shopTheLookHotspotPanelH'},
      transform: {
        md: 'translateY(-50%) translateX(var(--chakra-space-20, 5rem))',
      },
      display: 'flex',
      flexDirection: 'column',
      bg: 'black',
      color: 'white',
      borderLeftWidth: 0,
      borderLeft: 'none',
      overflow: 'hidden',
    },

    hotspotPanelRoot: {
      position: 'relative',
      flex: 1,
      display: {base: 'grid', md: 'flex'},
      flexDirection: {md: 'column'},
      gridTemplateColumns: {base: 'minmax(0, 1fr)', md: undefined},
      gridTemplateRows: {base: 'minmax(0, 1fr)', md: undefined},
      minH: 0,
      minW: 0,
      h: '100%',
      overflow: 'hidden',
      bg: 'black',
    },

    hotspotPanelMediaShell: {
      gridColumn: {base: '1', md: 'auto'},
      gridRow: {base: '1', md: 'auto'},
      order: {md: 1},
      position: 'relative',
      flex: {md: '1 1 0%'},
      w: '100%',
      aspectRatio: {base: 'auto', md: 'auto'},
      minH: 0,
      minW: 0,
      zIndex: 0,
      overflow: 'hidden',
    },

    hotspotPanelNativeVideo: {
      position: 'absolute',
      inset: 0,
      w: '100%',
      h: '100%',
      minW: '100%',
      minH: '100%',
      maxW: 'none',
      maxH: 'none',
      objectFit: 'cover',
      objectPosition: 'center',
      display: 'block',
    },

    hotspotPanelImg: {
      position: 'absolute',
      inset: 0,
      w: '100%',
      h: '100%',
      minW: '100%',
      minH: '100%',
      maxW: 'none',
      maxH: 'none',
      objectFit: 'cover',
      objectPosition: 'center',
      display: 'block',
    },

    hotspotPanelMediaFallback: {
      position: 'absolute',
      inset: 0,
      w: '100%',
      h: '100%',
      minH: 'shopTheLookMediaFallbackMinH',
      bg: 'gray.800',
    },

    hotspotPanelEmbedVideo: {
      'position': 'absolute',
      'inset': 0,
      'w': '100%',
      'h': '100%',
      'minH': '100%',
      'overflow': 'hidden',
      '& .amb-video': {
        aspectRatio: 'unset !important',
        width: '100% !important',
        height: '100% !important',
        minHeight: '100% !important',
        minWidth: '100% !important',
        maxWidth: 'none !important',
        maxHeight: 'none !important',
      },
      '& .amb-video iframe, & .amb-video img': {
        position: 'absolute !important',
        top: '50% !important',
        left: '50% !important',
        width: '120% !important',
        height: '120% !important',
        maxWidth: 'none !important',
        maxHeight: 'none !important',
        transform: 'translate(-50%, -50%) scale(1.08) !important',
        transformOrigin: 'center center !important',
      },
    },

    hotspotPanelTextBlock: {
      gridColumn: {base: '1', md: 'auto'},
      gridRow: {base: '1', md: 'auto'},
      order: {md: 2},
      alignSelf: {base: 'start', md: 'auto'},
      position: {md: 'absolute'},
      top: {base: 0, md: 'auto'},
      bottom: {md: 0},
      left: {base: 0, md: 0},
      right: {base: 0, md: 0},
      maxW: {md: 'xl'},
      zIndex: 2,
      p: {base: 6, md: 8},
      pt: {base: 8, md: 8},
      pb: {base: 6, md: 10},
      color: '#ffffff',
      bg: {
        base: 'linear-gradient(180deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.4) 42%, rgba(0,0,0,0.12) 72%, transparent 100%)',
        md: 'transparent',
      },
      textAlign: 'left',
      pointerEvents: 'none',
      sx: {
        '& > *': {
          pointerEvents: 'auto',
        },
      },
    },

    hotspotPanelTitle: {
      textStyle: 'labelXL',
      fontWeight: 'semibold',
      mb: 3,
      color: '#ffffff',
    },

    hotspotPanelBody: {
      'color': '#ffffff',
      'fontSize': {base: 'sm', md: 'md'},
      'lineHeight': 'tall',
      '& a': {
        color: '#ffffff',
        textDecoration: 'underline',
      },
      '& p': {
        mb: 2,
      },
      '& .amb-markup h1, & .amb-markup .h1': {
        color: '#ffffff !important',
      },
      '& .amb-markup p, & .amb-markup li, & .amb-markup h2, & .amb-markup h3, & .amb-markup h4, & .amb-markup h5, & .amb-markup h6': {
        color: '#ffffff !important',
      },
      '& .amb-markup strong, & .amb-markup em, & .amb-markup span, & .amb-markup blockquote, & .amb-markup blockquote p': {
        color: '#ffffff !important',
      },
      '& .amb-markup .chakra-text, & .rich-text-content .chakra-text, & .rich-text-content p, & .rich-text-content li': {
        color: '#ffffff !important',
      },
    },

    hotspotPanelCloseBtn: {
      position: 'absolute',
      top: {base: 3, md: 4},
      right: {base: 3, md: 4},
      zIndex: 3,
      borderRadius: '0',
      minW: 11,
      minH: 11,
      color: 'white',
      bg: 'transparent',
      _hover: {
        bg: 'transparent',
      },
      _focusVisible: {
        boxShadow: '0 0 0 2px rgba(255,255,255,0.8)',
      },
    },

    hotspotPanelNavStack: {
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      bottom: {base: 4, md: 6},
      right: {base: 4, md: 6},
      zIndex: 3,
      gap: 1,
    },

    hotspotPanelNavBtn: {
      borderRadius: '0',
      minW: 11,
      minH: 11,
      color: 'white',
      bg: 'transparent',
      _hover: {
        bg: 'whiteAlpha.200',
      },
      _focusVisible: {
        boxShadow: '0 0 0 2px rgba(255,255,255,0.8)',
      },
    },

    hotspotAnchor: {
      position: 'absolute',
      transform: 'translate(-50%, -50%)',
      zIndex: 2,
    },

    hotspotContentDot: {
      w: {base: 10, md: 16},
      h: {base: 10, md: 16},
      minW: {base: 10, md: 16},
      minH: {base: 10, md: 16},
      p: 0,
      boxSizing: 'border-box',
      borderRadius: 'full',
      bg: 'transparent',
      border: 'none',
      borderWidth: 0,
      boxShadow: 'none',
      outline: 'none',
      outlineOffset: 0,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'none',
      appearance: 'none',
      color: 'inherit',
      _hover: {
        bg: 'transparent',
        border: 'none',
        boxShadow: 'none',
        outline: 'none',
        transform: 'none',
      },
      _active: {
        bg: 'transparent',
        border: 'none',
        boxShadow: 'none',
        outline: 'none',
        transform: 'none',
      },
      _focus: {
        bg: 'transparent',
        border: 'none',
        boxShadow: 'none',
        outline: 'none',
      },
      _focusVisible: {
        bg: 'transparent',
        border: 'none',
        boxShadow: 'none',
        outline: 'none',
      },
    },

    hotspotDot: {
      w: 6,
      h: 6,
      minW: 6,
      minH: 6,
      p: 0,
      boxSizing: 'border-box',
      borderRadius: 'full',
      bg: 'transparent',
      border: 'none',
      borderWidth: 0,
      boxShadow: 'none',
      outline: '1px solid #ffffff',
      outlineOffset: 0,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'transform 0.2s ease, background-color 0.2s ease, outline 0.2s ease',
      appearance: 'none',
      color: 'white',
      _hover: {
        transform: 'scale(1.06)',
        bg: '#000000',
        backgroundColor: '#000000',
        outline: '4px solid rgba(255, 255, 255, 0.5)',
        outlineOffset: 0,
        boxShadow: 'none',
      },
      _active: {
        bg: '#000000',
        backgroundColor: '#000000',
        outline: '4px solid rgba(255, 255, 255, 0.5)',
        outlineOffset: 0,
        boxShadow: 'none',
      },
      _focus: {
        outline: '1px solid #ffffff',
        outlineOffset: 0,
        boxShadow: 'none',
      },
      _focusVisible: {
        bg: '#000000',
        backgroundColor: '#000000',
        outline: '4px solid rgba(255, 255, 255, 0.5)',
        outlineOffset: 0,
        boxShadow: 'none',
      },
      sx: {
        '&&:not(:hover):not(:focus-visible)': {
          border: 'none',
          boxShadow: 'none',
          outline: '1px solid #ffffff',
          outlineOffset: 0,
        },
        '&&:focus:not(:focus-visible)': {
          border: 'none',
          boxShadow: 'none',
          outline: '1px solid #ffffff',
          outlineOffset: 0,
          backgroundColor: 'transparent',
        },
        '&&:focus:not(:focus-visible):hover': {
          backgroundColor: '#000000',
          border: 'none',
          outline: '4px solid rgba(255, 255, 255, 0.5)',
          outlineOffset: 0,
          boxShadow: 'none',
        },
        '&&:hover': {
          backgroundColor: '#000000',
          border: 'none',
          outline: '4px solid rgba(255, 255, 255, 0.5)',
          outlineOffset: 0,
          boxShadow: 'none',
        },
        '&&:active': {
          backgroundColor: '#000000',
          border: 'none',
          outline: '4px solid rgba(255, 255, 255, 0.5)',
          outlineOffset: 0,
          boxShadow: 'none',
        },
        '&&:focus-visible': {
          backgroundColor: '#000000',
          border: 'none',
          outline: '4px solid rgba(255, 255, 255, 0.5)',
          outlineOffset: 0,
          boxShadow: 'none',
        },
        '&&[data-hover]': {
          backgroundColor: '#000000',
          border: 'none',
          outline: '4px solid rgba(255, 255, 255, 0.5)',
          outlineOffset: 0,
          boxShadow: 'none',
        },
        '&&[data-active]': {
          backgroundColor: '#000000',
          border: 'none',
          outline: '4px solid rgba(255, 255, 255, 0.5)',
          outlineOffset: 0,
          boxShadow: 'none',
        },
        '&&[data-focus]': {
          outlineOffset: 0,
        },
        '&&[aria-expanded="true"]': {
          backgroundColor: '#000000',
          border: 'none',
          outline: '4px solid rgba(255, 255, 255, 0.5)',
          outlineOffset: 0,
          boxShadow: 'none',
        },
      },
    },

    hotspotPopoverContent: {
      bg: 'transparent',
      border: 'none',
      borderWidth: 0,
      borderRadius: '0',
      boxShadow: 'none',
      w: 'auto',
      minW: 0,
      maxW: 'min(320px, calc(100vw - 32px))',
      zIndex: 20,
      overflow: 'visible',
      p: 0,
      m: 0,
      outline: 'none',
      filter: 'none',
      _focus: {
        boxShadow: 'none',
        outline: 'none',
      },
      _hover: {
        boxShadow: 'none',
      },
      _focusVisible: {
        boxShadow: 'none',
        outline: 'none',
      },
    },

    hotspotPopoverBody: {
      p: 0,
      m: 0,
      w: '100%',
      bg: 'transparent',
      border: 'none',
      boxShadow: 'none',
      outline: 'none',
    },

    hotspotPopoverCard: {
      bg: 'white',
      border: '1px solid',
      borderColor: 'brandColors.greyscale10',
      borderRadius: '0',
      boxShadow: 'none',
      w: '100%',
      maxW: 'min(320px, calc(100vw - 32px))',
      overflow: 'hidden',
    },

    hotspotPopoverLink: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      p: 4,
      w: '100%',
      boxSizing: 'border-box',
      textDecoration: 'none',
      color: 'inherit',
      minW: 0,
      transition: 'background 0.15s ease',
      _hover: {
        textDecoration: 'none',
        bg: 'gray.50',
      },
    },

    hotspotPopoverThumb: {
      flex: '0 0 16',
      w: 16,
      h: 16,
      borderRadius: '0',
      overflow: 'hidden',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'stretch',
      boxSizing: 'border-box',
    },

    hotspotPopoverTextCol: {
      flex: 1,
      minW: 0,
      alignSelf: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: 1,
    },

    hotspotPopoverTitle: {
      fontWeight: 600,
      fontSize: 'md',
      lineHeight: 'shorter',
      color: 'brandColors.greyscale90',
      noOfLines: 2,
    },

    hotspotPopoverMeta: {
      fontSize: 'xs',
      lineHeight: 'shorter',
      fontWeight: 'normal',
      color: 'brandColors.greyscale70',
      noOfLines: 1,
    },

    hotspotPopoverChevron: {
      flexShrink: 0,
      alignSelf: 'center',
      color: 'brandColors.primaryDarkLinen',
    },

    gradientBar: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      h: {base: 17, md: 21},
      zIndex: 3,
      bgGradient: 'linear(to-t, blackAlpha.600, transparent)',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      p: {base: 3, md: 5},
      pointerEvents: 'none',
      sx: {
        '& > *': {
          pointerEvents: 'auto',
        },
      },
    },

    shopActions: {
      display: 'flex',
      gap: 2,
      alignItems: 'center',
    },

    drawerOverlay: {
      bg: 'transparent',
      backdropFilter: 'none',
      sx: {
        WebkitBackdropFilter: 'none',
      },
    },

    drawerHeroWrap: {
      position: 'relative',
      w: '100%',
      flexShrink: 0,
      mt: 0,
      pt: 0,
    },

    drawerHeroMedia: {
      position: 'relative',
      w: '100%',
      aspectRatio: '4 / 3',
      maxH: {base: 'min(280px, 42dvh)', md: 'min(280px, 42vh)'},
      overflow: 'hidden',
      bg: 'gray.100',
      lineHeight: 0,
      flexShrink: 0,
    },

    drawerHeroImage: {
      position: 'absolute',
      inset: 0,
      w: '100%',
      h: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
      display: 'block',
    },

    drawerContent: {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden',
      h: {base: '100dvh', md: 'auto'},
      maxH: {base: '100dvh', md: '100vh'},
      minH: {base: 0, md: 'auto'},
      boxShadow: 'none',
      maxW: {base: '100%', md: 'shopTheLookDrawerMaxW'},
      pt: 0,
      background:
        'linear-gradient(0deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.7) 100%), #F7F5F0',
    },

    drawerHeader: {
      py: {base: 4, md: 5},
      px: {base: 4, md: 5},
      position: 'relative',
      boxShadow: 'none',
    },

    drawerHeaderProducts: {
      pt: {base: 5, md: 4.75},
      pb: {base: 4.5, md: 4.5},
      pl: {base: 5, md: 5, lg: 5},
      pr: {base: 5, md: 5, lg: 5},
      position: 'relative',
      boxShadow: 'none',
      bg: {base: 'white', md: 'transparent'},
      flexShrink: 0,
      sx: {
        '& .chakra-modal__close-btn': {
          width: 5,
          minH: 5,
          height: 5,
          minW: 5,
        },
      },
    },

    drawerProductsHeaderStack: {
      flexDirection: 'column',
      alignItems: 'stretch',
      width: '100%',
      gap: 4.75,
      minW: 0,
    },

    drawerHeaderRow: {
      width: '100%',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 4,
      minW: 0,
    },

    drawerTitle: {
      flex: 1,
      minW: 0,
      textStyle: 'labelXL',
      lineHeight: {base: '150%', md: '140%'},
      color: 'brandColors.greyscale90',
      letterSpacing: 'normal',
    },

    drawerBodyTitleRow: {
      width: '100%',
      minW: 0,
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: {base: 3, md: 4},
      mt: {base: 2.5, md: 5},
    },

    drawerBodyTitle: {
      flex: 1,
      minW: 0,
      textStyle: 'labelXL',
      color: 'brandColors.greyscale90',
      lineHeight: {base: '150%', md: '140%'},
      letterSpacing: 'normal',
    },

    drawerItemCount: {
      flexShrink: 0,
      textStyle: 'bodyS',
      color: 'brandColors.greyscale90',
      whiteSpace: 'nowrap',
      lineHeight: {base: '150%', md: '140%'},
      letterSpacing: 'normal',
    },

    drawerCloseButtonFlow: {
      position: 'relative',
      top: 'auto',
      right: 'auto',
      borderRadius: '0',
      color: 'brandColors.greyscale90',
      minW: 11,
      minH: 11,
      sx: {
        '& svg, & .chakra-icon': {
          width: 5,
          height: 5,
          flexShrink: 0,
        },
      },
      _hover: {
        bg: 'transparent',
      },
      _focusVisible: {
        boxShadow: 'none',
        outline: '2px solid',
        outlineColor: 'brandColors.greyscale90',
        outlineOffset: 0.5,
      },
    },

    drawerBody: {
      flex: 1,
      overflowY: 'auto',
      pt: {base: 5, lg: 5},
      pb: {base: 5, lg: 5},
      pl: {base: 0, lg: 0},
      pr: {base: 0, lg: 0},
    },

    drawerBodyProducts: {
      flex: 1,
      minH: 0,
      overflowY: 'auto',
      pt: 0,
      pl: 5,
      pr: 5,
      pb: {base: 5, md: 25},
      WebkitOverflowScrolling: 'touch',
    },

    drawerFooter: {
      display: 'flex',
      position: {base: 'relative', md: 'absolute'},
      left: {md: 0},
      right: {md: 0},
      bottom: {md: 0},
      zIndex: 2,
      flexShrink: 0,
      minH: {base: 'auto', md: 31.25},
      alignItems: 'flex-end',
      gap: 2,
      bg: 'transparent',
      borderTopWidth: 0,
      boxShadow: 'none',
      backgroundImage:
        'linear-gradient(180deg, rgba(247, 245, 240, 0) 0%, #F7F5F0 24.12%)',
      backgroundColor: 'transparent',
      backgroundRepeat: 'no-repeat',
      padding: 5,
      sx: {
        '&&': {
          padding: 0,
          paddingLeft: 5,
          paddingRight: 5,
          paddingTop: 0,
          paddingBottom: {
            base: 'calc(20px + env(safe-area-inset-bottom, 0px))',
            md: 5,
          },
        },
      },
    },

    drawerFooterBtnBag: {
      variant: 'filled-light',
      size: 'sm',
      display: 'flex',
      flex: '1 0 0',
      minW: 0,
      w: '100%',
      h: 13.5,
      minH: 13.5,
      maxH: 13.5,
      py: 4,
      px: 12,
      justifyContent: 'center',
      alignItems: 'center',
      gap: 2,
      borderRadius: '4px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.greyscale',
      bg: 'brandColors.greyscale',
      color: 'white',
      fontFamily: 'heading',
      fontSize: 'md',
      fontStyle: 'normal',
      fontWeight: 'semibold',
      lineHeight: '140%',
      textAlign: 'center',
      fontVariantNumeric: 'lining-nums proportional-nums',
      sx: {
        boxSizing: 'border-box',
        '& .chakra-button__icon': {
          marginInlineEnd: 0,
          margin: 0,
          width: 4.5,
          height: 4.5,
          minW: 4.5,
          minH: 4.5,
          flexShrink: 0,
        },
        '& svg, & .chakra-icon': {
          width: 4.5,
          height: 4.5,
          aspectRatio: '1 / 1',
          flexShrink: 0,
        },
      },
    },

    drawerProductRow: {
      display: 'flex',
      gap: {base: 3, md: 4},
      py: 2.5,
      alignItems: 'flex-start',
      _first: {
        pt: 0,
      },
      _last: {
        pb: 0,
      },
    },

    drawerProductImage: {
      w: {base: 24, md: 32.5},
      h: {base: 24, md: 32.5},
      flexShrink: 0,
      borderRadius: '0',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'stretch',
    },

    drawerProductImageInner: {
      w: '100%',
      h: '100%',
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'stretch',
    },

    drawerProductMain: {
      flex: 1,
      minW: 0,
      pr: 0,
    },

    drawerProductTitle: {
      textStyle: 'labelDefault',
      color: 'brandColors.greyscale90',
      fontVariantNumeric: 'lining-nums proportional-nums',
      noOfLines: 3,
      mt: 2,
    },

    drawerProductTitleLink: {
      color: 'inherit',
      textDecoration: 'none',
      _hover: {
        textDecoration: 'underline',
        color: 'brandColors.greyscale90',
      },
    },

    drawerProductSpec: {
      textStyle: 'labelS',
      color: 'brandColors.greyscale60',
      mt: {base: 1.5, md: 2},
      noOfLines: 2,
    },

    drawerSwatchRow: {
      alignItems: 'center',
      gap: 3,
      mt: 3,
    },

    drawerSwatchDot: {
      w: 3.5,
      h: 3.5,
      minW: 3.5,
      borderRadius: 'full',
      borderWidth: '1px',
      borderColor: 'brandColors.greyscale10',
      flexShrink: 0,
    },

    drawerSwatchLabel: {
      textStyle: 'labelS',
      color: 'brandColors.greyscale90',
    },

    drawerPriceRow: {
      alignItems: 'baseline',
      gap: 2,
      flexWrap: 'wrap',
      mt: 3,
      mb: 2,
    },

    drawerCurrentPrice: {
      textStyle: 'labelDefault',
      color: 'brandColors.greyscale90',
      fontVariantNumeric: 'lining-nums proportional-nums',
    },

    drawerTotalValue: {
      fontSize: 'xs',
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: '120%',
      color: 'brandColors.greyscale50',
    },

    drawerTotalValueStrikethrough: {
      fontSize: 'xs',
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: '120%',
      color: 'brandColors.greyscale50',
      textDecoration: 'line-through',
    },

    drawerProductActions: {
      flexDirection: 'column',
      gap: 2,
      flexShrink: 0,
      pt: 0.5,
    },

    drawerIconButton: {
      variant: 'outlined-light',
      size: 'sm',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2.5,
      aspectRatio: '1 / 1',
      lineHeight: 1,
      w: 11,
      h: 11,
      minW: 11,
      minH: 11,
      maxW: 11,
      maxH: 11,
      py: 2.5,
      px: 3,
      borderRadius: '4px',
      borderWidth: '1px',
      borderColor: 'brandColors.linen03',
      bg: 'white',
      color: 'brandColors.primaryDarkLinen',
      sx: {
        boxSizing: 'border-box',
        '& svg, & .chakra-icon': {
          width: 5,
          height: 5,
          flexShrink: 0,
        },
      },
      _hover: {
        bg: 'gray.50',
        color: 'brandColors.primaryDarkLinen',
        borderColor: 'brandColors.linen03',
      },
      _active: {
        bg: 'gray.100',
        color: 'brandColors.primaryDarkLinen',
        borderColor: 'brandColors.linen03',
      },
      _focusVisible: {
        boxShadow: '0 0 0 3px rgba(27, 26, 24, 0.2)',
      },
      _disabled: {
        bg: 'white',
        color: 'brandColors.greyscale40',
        borderColor: 'brandColors.linen03',
        opacity: 1,
        cursor: 'not-allowed',
      },
    },

    contentCarousel: {
      display: 'flex',
      flexDirection: 'row',
      gap: 4,
      overflowX: 'auto',
      scrollSnapType: 'x mandatory',
      pb: 2,
      sx: {
        scrollbarWidth: 'thin',
      },
    },

    contentSlide: {
      flex: '0 0 100%',
      scrollSnapAlign: 'start',
      minW: '100%',
    },

    quickAddBtn: {
      position: 'absolute',
      bottom: {base: 5, md: 6},
      right: {base: 5, md: 6},
      zIndex: 3,
    },
  },

  variants: {
    /** Standalone block on a page — outer padding / max width per Figma */
    standaloneContent: {
      root: {
        maxW: 'container.xxl',
        mx: 'auto',
        px: {base: 5, md: 10, lg: 20},
        py: {base: 5, lg: 10},
      },
      layerContainer: {
        maxW: {md: '100%'},
        mx: {md: 'auto'},
      },
    },
  },
};

export default ShopTheLookBlock;
